import {
  ActivitySummary,
  CompletionCriterion,
  CompletionCriterionDetails,
  CompletionCriterionRequest,
  CompletionCriterionUpdateRequest,
  MoveCompletionCriterionRequest,
  CreateDocumentRequest,
  CreateProjectRequest,
  CreateLinkRequest,
  UpdateLinkRequest,
  Link,
  Document,
  MergeFunctionRequest,
  MilestoneSummary,
  Phase,
  PhaseCreate,
  PhaseUpdate,
  ProjFunction,
  ProjFunctionRequest,
  Project,
  ProjectFunction,
  ProjectSummary,
  ProjectsService,
  ProjectReport,
  ReportsService,
  Tag,
  UpdateDocumentRequest,
  UpdateProjFunctionRequest,
  UpdateProjectRequest,
  UserSummary,
  Workstream,
  WorkstreamCreate,
  ActionRequest,
  Action,
  ActionUpdateRequest,
  ProjectCalendarSyncToggleRequest,
  ProjectDetails,
  ProjectNonWorkingDay,
  CreateProjectNonWorkingDayRequest,
  UpdateProjectNonWorkingDayRequest,
  ActionDetails,
  UpdateProjectDetailsRequest,
  MoveActionRequest,
  UpdateProjectFromTemplateRequest,
  AddWorkstreamFromTemplateRequest,
  UpdateMirrorActivityRequest,
  MirrorActivityRelationRequest,
  MirrorActivityRequest,
  MirrorMilestoneRequest,
  RemoveMirrorActivityRelationRequest,
  ProjectShiftElementsRequest,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { TreeNode } from 'primevue/treenode';
import { createConcurrencyQueue } from '@/utils/helpers/concurrencyQueue';
import {
  blobToBase64,
  compressImageToJpeg,
} from '@/utils/helpers/imageCompressionHelper';
import {
  fetchProjectImageBlobUrl,
  invalidateBlobCache,
} from '@/utils/helpers/userApiHelpers';
import { diff } from '@/utils/helpers/various';
import { useActivityProjectStore } from '../activityProjectStore';
import { useProfileStore } from '../profileStore';
import { useProjectRulesStore } from '../projectRulesStore';
import { useUserStore } from '../userStore';

export type EnhancedMilestone = MilestoneSummary & {
  _phaseId: string;
  _final: boolean;
  isFinal: boolean;
};

export type EnhancedProjectFunction = ProjectFunction & {
  user: UserSummary;
};

export type ProjectsStoreState = {
  projects: Project[];
  projectSummaries: ProjectSummary[];
  projectsDetails: ProjectDetails[];
  projectReports: ProjectReport[];
  members: Record<string, UserSummary[]>;
  links: Link[];
  isListView: boolean;
  favoriteProjectIds: string[];
  lastToggledProjectId: string;
  // blob URL cache: undefined = not loaded, null = no image, string = blob URL
  imageUrls: Record<string, string | null | undefined>;
};

const enqueueProjectImageFetch = createConcurrencyQueue(10);

/**
 * Keeps a project's non-working days in the same order the API returns them
 * (start date, then id for a stable tie-break), so the editor list doesn't
 * reshuffle between a local mutation and the next reload.
 */
function sortNonWorkingDays(
  nonWorkingDays: ProjectNonWorkingDay[],
): ProjectNonWorkingDay[] {
  return [...nonWorkingDays].sort(
    (a, b) =>
      a.start_date.localeCompare(b.start_date) || a.id.localeCompare(b.id),
  );
}

export const useProjectStore = defineStore('projectStore', {
  state: (): ProjectsStoreState => ({
    projects: [],
    projectSummaries: [],
    projectsDetails: [],
    projectReports: [],
    members: {},
    links: [],
    isListView: false,
    favoriteProjectIds: [],
    lastToggledProjectId: '',
    imageUrls: {},
  }),

  getters: {
    project(state) {
      return (projectId: string): Project => {
        const project = state.projects.find(
          (project) => project.id === projectId,
        );
        if (!project) {
          throw new Error(`Project ${projectId} not found`);
        }

        return project;
      };
    },

    projectSummary(state) {
      return (projectId: string) => {
        const summary = state.projectSummaries.find(
          ({ id }) => id === projectId,
        );

        if (!summary) {
          throw new Error(`Project ${projectId} not found`);
        }

        return summary;
      };
    },

    projectDetails(state) {
      return (projectId: string): ProjectDetails => {
        const project = state.projectsDetails.find(
          (project) => project.id === projectId,
        );
        if (!project) {
          throw new Error(`Project ${projectId} not found`);
        }

        return project;
      };
    },

    hasProject(state) {
      return (id: string) => {
        return state.projects.some((project) => project.id === id);
      };
    },

    hasProjectSummary(state) {
      return (id: string) => {
        return state.projectSummaries.some((project) => project.id === id);
      };
    },

    projectTitle() {
      return (projectId: string) => {
        if (this.hasProjectSummary(projectId)) {
          return this.projectSummary(projectId).title;
        }
        if (this.hasProject(projectId)) {
          return this.project(projectId).title;
        }

        return '';
      };
    },

    projectMembers(state) {
      return (projectId: string) => {
        const members = state.members[projectId];
        if (!members) {
          throw new Error(`Project ${projectId} members not found`);
        }

        return members;
      };
    },

    hasMembers(state) {
      return (projectId: string) => {
        return !!state.members[projectId];
      };
    },

    hasActivitySummary() {
      return (projectId: string, activityId: string) => {
        const activity = this.project(projectId).activities.find(
          (activity) => activity.id === activityId,
        );

        return !!activity;
      };
    },

    activitySummary() {
      return (projectId: string, activityId: string) => {
        const activity = this.project(projectId).activities.find(
          (activity) => activity.id === activityId,
        );
        if (!activity) {
          throw new Error(`Activity ${projectId}/${activityId} not found`);
        }

        return activity;
      };
    },

    activitiesInPhase() {
      return (projectId: string, phaseId: string) => {
        const project = this.project(projectId);

        return project.activities.filter(({ phase }) => phase === phaseId);
      };
    },

    functions() {
      return (projectId: string) => {
        return this.project(projectId).functions;
      };
    },

    projectFunction() {
      return (projectId: string, functionId: string) => {
        const projectFunction = this.project(projectId).functions.find(
          ({ id }) => id === functionId,
        );
        if (!projectFunction) {
          throw new Error(
            `Missing projectFunction (${functionId}) in template (${projectId}) `,
          );
        }

        return projectFunction;
      };
    },

    hasProjectFunction() {
      return (projectId: string, functionId: string) =>
        !!this.project(projectId).functions.find(({ id }) => id === functionId);
    },

    phaseStart() {
      return (projectId: string, phaseId: string) => {
        const project = this.project(projectId);
        const phases = project.phases.slice();
        const phase = phases.find(({ id }) => id === phaseId);
        if (!phase) {
          throw new Error('Phase ' + phaseId + ' not found');
        }
        let startDate = project.start_date;
        phases.forEach(({ deadline }) => {
          if (deadline > startDate && deadline < phase.deadline) {
            startDate = deadline;
          }
        });

        return startDate;
      };
    },

    milestonePhase() {
      return (projectId: string, milestoneId: string) => {
        const phases = this.project(projectId).phases;
        const phase = phases.find((phase) => {
          return (
            phase.final_milestone.id === milestoneId ||
            phase.intermediate_milestones.find(({ id }) => id === milestoneId)
          );
        });
        if (!phase) {
          throw new Error('Phase of milestone' + milestoneId + 'not found');
        }

        return phase;
      };
    },

    hasMilestoneSummary() {
      return (projectId: string, milestoneId: string) => {
        const phases = this.project(projectId).phases;

        return phases.some(
          (phase) =>
            phase.final_milestone.id === milestoneId ||
            phase.intermediate_milestones.some(({ id }) => id === milestoneId),
        );
      };
    },

    milestoneSummary() {
      return (projectId: string, milestoneId: string) => {
        const phases = this.project(projectId).phases.slice();
        let summary: MilestoneSummary | undefined;
        while (!summary && phases.length) {
          const phase = phases.pop() as Phase;
          summary =
            (phase.final_milestone.id === milestoneId
              ? phase.final_milestone
              : null) ||
            phase.intermediate_milestones.find(({ id }) => id === milestoneId);
        }
        if (summary) {
          return summary;
        }
        throw new Error('MilestoneSummary not found: ' + milestoneId);
      };
    },

    milestones() {
      return (projectId: string): EnhancedMilestone[] => {
        const project = this.project(projectId);

        const milestones = [] as EnhancedMilestone[];
        project.phases.forEach((phase) => {
          const phaseMilestones = phase.intermediate_milestones.map((d) => ({
            ...d,
            _phaseId: phase.id,
            isFinal: false,
            _final: false,
          }));
          milestones.push(...phaseMilestones);
          milestones.push({
            ...phase.final_milestone,
            _phaseId: phase.id,
            isFinal: true,
            _final: true,
          });
        });

        return milestones;
      };
    },

    milestone() {
      return (projectId: string, milestoneId: string): EnhancedMilestone => {
        const milestones = this.milestones(projectId);
        const milestone = milestones.find((m) => m.id === milestoneId);
        if (!milestone) {
          throw new Error(`Milestone ${milestoneId} not found`);
        }

        return milestone;
      };
    },

    allTags() {
      return (projectId: string): Tag[] =>
        this.project(projectId)?.tags.sort((a, b) =>
          a.title.localeCompare(b.title),
        ) || [];
    },

    allLinks() {
      return (projectId: string): Link[] =>
        this.project(projectId)?.links.sort((a, b) =>
          a.title.localeCompare(b.title),
        ) || [];
    },

    allDocuments() {
      return (projectId: string): Document[] =>
        this.project(projectId)?.documents.sort((a, b) =>
          a.title.localeCompare(b.title),
        ) || [];
    },

    tag() {
      return (projectId: string, tagId: string): Tag => {
        const tag = this.allTags(projectId).find((tag) => tag.id === tagId);
        if (!tag) {
          throw new Error('Missing tag in projectStore');
        }

        return tag;
      };
    },

    allTopicTags() {
      return (projectId: string): Tag[] => {
        return this.allTags(projectId).filter((tag) => tag.topic);
      };
    },

    allNonTopicTags() {
      return (projectId: string): Tag[] => {
        return this.allTags(projectId).filter((tag) => !tag.topic);
      };
    },

    allReferencedProjects() {
      return (
        projectId: string,
      ): Array<{ project_title: string; source_project_id: string }> => {
        const projectData = this.project(projectId);
        const mirroredActivities = projectData?.mirrored_activities ?? [];
        const mirroredMilestones = projectData?.mirrored_milestones ?? [];

        const pairMap = new Map<string, string>();
        for (const activity of mirroredActivities) {
          pairMap.set(activity.source_project_id, activity.project_title);
        }
        for (const milestone of mirroredMilestones) {
          if (milestone.source_project_id) {
            pairMap.set(milestone.source_project_id, milestone.project_title);
          }
        }

        return Array.from(pairMap.entries()).map(
          ([source_project_id, project_title]) => ({
            source_project_id,
            project_title,
          }),
        );
      };
    },

    link() {
      return (projectId: string, linkId: string) => {
        const project = this.project(projectId);
        const link = project.links.find(({ id }) => id === linkId);
        if (!link) {
          throw new Error(`Missing link ${linkId} in template ${projectId}`);
        }

        return link;
      };
    },

    hasLink() {
      return (projectId: string, linkId: string) => {
        const project = this.project(projectId);

        return project.links.some(({ id }) => id === linkId);
      };
    },

    criterion() {
      return (projectId: string, activityId: string, criterionId: string) => {
        const activity = this.activitySummary(projectId, activityId);
        const criterion = activity.completion_criteria.find(
          ({ id }) => criterionId === id,
        );
        if (!criterion) {
          throw new Error(
            `Missing completion criterion (${criterionId}) in activity (${activityId}) project`,
          );
        }

        return criterion;
      };
    },

    action() {
      return (projectId: string, activityId: string, actionId: string) => {
        const activity = this.activitySummary(projectId, activityId);
        const action = activity.actions.find(({ id }) => actionId === id);
        if (!action) {
          throw new Error('Missing action in activity project');
        }

        return action;
      };
    },

    phase() {
      return (projectId: string, phaseId: string) => {
        const project = this.project(projectId);
        const phase = project.phases.find(({ id }) => id === phaseId);
        if (!phase) {
          throw new Error(`Missing phase ${phaseId} in project ${projectId}`);
        }

        return phase;
      };
    },

    workstream() {
      return (projectId: string, workstreamId: string): Workstream => {
        const project = this.project(projectId);
        const workstream = project.workstreams.find(
          (w) => w.id === workstreamId,
        );
        if (!workstream) {
          throw new Error(`Workstream ${workstreamId} not found`);
        }

        return workstream;
      };
    },

    workstreamPosition() {
      return (projectId: string, workstreamId: string): number => {
        const workstreams = this.project(projectId).workstreams;
        const position = workstreams.findIndex(({ id }) => id === workstreamId);
        if (position < 0) {
          throw new Error(`Workstream ${workstreamId} not found`);
        }

        return position;
      };
    },

    // sorted template activities by sequence and workstreamPosition
    sortedActivities() {
      return (projectId: string): ActivitySummary[] => {
        const activities = this.project(projectId).activities.slice();

        return activities.sort((a, b) => {
          const aDate = a.current_deadline;
          const bDate = b.current_deadline;
          if (aDate === bDate) {
            const aWsPos = this.workstreamPosition(projectId, a.workstream);
            const bWsPos = this.workstreamPosition(projectId, b.workstream);
            if (aWsPos === bWsPos) {
              return 0;
            }

            return aWsPos < bWsPos ? 1 : -1;
          }

          return aDate < bDate ? 1 : -1;
        });
      };
    },

    previousActivity() {
      return (
        projectId: string,
        activityId: string,
      ): ActivitySummary | undefined => {
        const sortedActivities = this.sortedActivities(projectId);
        const position = sortedActivities.findIndex(
          ({ id }) => activityId === id,
        );

        return sortedActivities[position - 1];
      };
    },

    nextActivity() {
      return (
        projectId: string,
        activityId: string,
      ): ActivitySummary | undefined => {
        const sortedActivities = this.sortedActivities(projectId);
        const position = sortedActivities.findIndex(
          ({ id }) => activityId === id,
        );

        return sortedActivities[position + 1];
      };
    },

    frozen() {
      return useProfileStore().isLoading;
    },

    successorsList() {
      return (projectId: string, activityId: string): ActivitySummary[] => {
        const activities = this.project(projectId).activities;

        return activities.filter(({ predecessors }) =>
          predecessors.includes(activityId),
        );
      };
    },
  },

  actions: {
    write(project: Project) {
      const persistedProject = this.projects.find(
        (proj) => proj.id === project.id,
      );

      if (persistedProject) {
        Object.assign(persistedProject, project);
      } else {
        this.projects.push(project);
      }

      if (!project.has_image_uploaded) {
        const oldUrl = this.imageUrls[project.id];
        if (oldUrl) {
          URL.revokeObjectURL(oldUrl);
          void invalidateBlobCache(`/v1/projects/${project.id}/image`);
        }
        this.imageUrls[project.id] = null;
      }

      return this.project(project.id);
    },

    writeActivitySummary(projectId: string, activity: ActivitySummary) {
      const project = this.project(projectId);
      const persistedActivity = project.activities.find(
        ({ id }) => id === activity.id,
      );
      if (persistedActivity) {
        Object.assign(persistedActivity, activity);
      } else {
        const project = this.project(projectId);
        project.activities.push(activity);
      }

      return this.activitySummary(projectId, activity.id);
    },

    writeActivityTags(
      projectId: string,
      activityId: string,
      options: { add?: string[]; remove?: string[] },
    ) {
      const activity = this.activitySummary(projectId, activityId);
      const { add, remove } = options;

      if (add) {
        add.forEach((tagId) => {
          if (!activity.tags.includes(tagId)) {
            activity.tags.push(tagId);
          }
        });
      }

      if (remove) {
        activity.tags = activity.tags.filter(
          (tagId) => !remove.includes(tagId),
        );
      }
    },

    async writeMirroredActivitySummary(
      projectId: string,
      mirroredActivityId: string,
      newWorkstreamId: string,
    ) {
      const project = this.project(projectId);
      const persistedMirroredActivity = project.mirrored_activities.find(
        ({ id }) => id === mirroredActivityId,
      );

      if (persistedMirroredActivity) {
        Object.assign(persistedMirroredActivity, {
          workstream_in_project: newWorkstreamId,
        });
      } else {
        await this.loadProject(projectId);
      }
    },

    writeAction(
      projectId: string,
      activityId: string,
      actionDetails: ActionDetails,
    ) {
      const activityProjectStore = useActivityProjectStore();
      const summary = this.activitySummary(projectId, activityId);
      const foundAction = summary.actions?.find(
        ({ id }) => id === actionDetails.id,
      );
      const action = mapCCOrActionDetailsToCCOrAction(actionDetails) as Action;
      if (!foundAction) {
        summary.actions.push(action);
      } else {
        if (action.state === 'underway') {
          this.updateActivityAndPhaseStateToUnderway(
            projectId,
            summary,
            action.status,
          );
        }
        Object.assign(foundAction, action);
      }

      if (activityProjectStore.hasActivity(projectId, activityId)) {
        activityProjectStore.writeAction(projectId, activityId, actionDetails);
      }

      return summary.actions;
    },

    writeCompletionCriterion(
      projectId: string,
      activityId: string,
      completionCriterionDetails: CompletionCriterionDetails,
    ) {
      const activityProjectStore = useActivityProjectStore();
      const activity = this.activitySummary(projectId, activityId);
      const criterion = activity.completion_criteria?.find(
        ({ id }) => id === completionCriterionDetails.id,
      );
      const completionCriterion = mapCCOrActionDetailsToCCOrAction(
        completionCriterionDetails,
      );

      if (!criterion) {
        activity.completion_criteria.push(completionCriterion);
      } else {
        if (completionCriterion.state === 'underway') {
          this.updateActivityAndPhaseStateToUnderway(
            projectId,
            activity,
            completionCriterion.status,
          );
        }
        Object.assign(criterion, completionCriterion);
      }
      if (activityProjectStore.hasActivity(projectId, activityId)) {
        activityProjectStore.writeCriterion(
          projectId,
          activityId,
          completionCriterionDetails,
        );
      }

      return activity.completion_criteria;
    },

    /**
     * Removes a criterion or action the server no longer has on the activity
     * (deleted, moved, converted) from both local copies: the summary, which
     * the map lists, and the cached details, which rules such as the reorder
     * check read. With only one of them updated, reordering failed until a
     * reload (WI #5801).
     */
    removeTaskLocally(
      projectId: string,
      activityId: string,
      key: 'completion_criteria' | 'actions',
      taskId: string,
    ) {
      const activityProjectStore = useActivityProjectStore();
      const lists: { id: string }[][] = [];
      if (
        this.hasProject(projectId) &&
        this.hasActivitySummary(projectId, activityId)
      ) {
        lists.push(this.activitySummary(projectId, activityId)[key]);
      }
      if (activityProjectStore.hasActivity(projectId, activityId)) {
        lists.push(activityProjectStore.activity(projectId, activityId)[key]);
      }
      // The two copies can share one array, so look the task up in each
      for (const tasks of lists) {
        const index = tasks.findIndex(({ id }) => id === taskId);
        if (index > -1) {
          tasks.splice(index, 1);
        }
      }
    },

    updateActivityAndPhaseStateToUnderway(
      projectId: string,
      activitySummary: ActivitySummary,
      newStatus: 'green' | 'yellow' | 'red' | null,
    ) {
      if (activitySummary.state === 'planned') {
        activitySummary.state = 'underway';
        activitySummary.status = newStatus;
        const phase = this.phase(projectId, activitySummary.phase);
        if (phase.state === 'planned') {
          phase.state = 'underway';
          phase.final_milestone.state = 'underway';
          phase.final_milestone.status = newStatus;
        }
      }
    },
    async projectTree(projectId: string): Promise<TreeNode[]> {
      if (this.projectSummaries.length === 0) {
        await this.loadList();
      }

      if (!this.hasProjectSummary(projectId)) {
        console.error(`Project ${projectId} not found`);

        return [];
      }

      const nodes: TreeNode[] = [];
      const projectSummary = this.projectSummary(projectId);
      if (projectSummary.parent_id) {
        const parentProject = this.projectSummary(projectSummary.parent_id);
        nodes.push({
          key: parentProject.id,
          label: parentProject.title,
          children: [],
          type: 'link',
        });

        this.projectSummaries.forEach((item) => {
          if (item.parent_id === parentProject.id) {
            nodes[0].children?.push({
              key: item.id,
              label: item.title,
              type: item.id === projectId ? 'active' : 'link',
            });
          }
        });

        return nodes;
      } else {
        nodes.push({
          key: projectSummary.id,
          label: projectSummary.title,
          children: [],
          type: 'active',
        });

        this.projectSummaries.forEach((item) => {
          if (item.parent_id === projectSummary.id) {
            nodes[0].children?.push({
              key: item.id,
              label: item.title,
              type: 'link',
            });
          }
        });

        return nodes;
      }
    },

    async loadList(participant?: string) {
      this.projectSummaries = await ProjectsService.getProjects(participant);

      return this.projectSummaries;
    },

    async loadProject(projectId: string) {
      const project = await ProjectsService.getProject(projectId);
      this.write(project);

      return this.project(projectId);
    },

    async loadFavoriteProjects() {
      const profileStore = useProfileStore();
      this.favoriteProjectIds = profileStore.settings.project_favorites;
    },

    async loadForExport(projectId: string) {
      return await ProjectsService.getProjectActivities(projectId);
    },

    async loadProjectsDetailsList() {
      this.projectsDetails = await ProjectsService.getallProjects();

      return this.projectsDetails;
    },

    async loadProjectReports() {
      this.projectReports = await ReportsService.getProjectReports();

      return this.projectReports;
    },

    async createProject(body: CreateProjectRequest & { file?: File }) {
      const { file, ...projectBody } = body;
      if (file) {
        const compressed = await compressImageToJpeg(file);
        projectBody.image_data = await blobToBase64(compressed);
      }
      const project = await ProjectsService.createProject(projectBody);
      await this.loadList();
      this.projects.push(project);

      return this.project(project.id);
    },

    async copyProject(
      projectId: string,
      newProjectName: string,
    ): Promise<string> {
      const response = await ProjectsService.copyProject(projectId, {
        new_project_name: newProjectName,
      });

      await this.loadList();

      return response.project_id;
    },

    async updateProjectFromTemplate(
      projectId: string,
      body: UpdateProjectFromTemplateRequest,
    ) {
      const project = await ProjectsService.updateProjectFromTemplate(
        projectId,
        body,
      );
      this.write(project);

      return this.project(projectId);
    },

    async updateDetails(
      projectId: string,
      body: UpdateProjectDetailsRequest & { file?: File },
    ) {
      const errors = useProjectRulesStore().validateUpdateProjectDetailsRequest(
        projectId,
        body,
      );

      if (errors) {
        throw errors;
      }

      const { file, ...projectBody } = body;
      if (file) {
        const compressed = await compressImageToJpeg(file);
        projectBody.image_data = await blobToBase64(compressed);
      }
      const project = await ProjectsService.updateProjectDetails(
        projectId,
        projectBody,
      );

      const persistedProject = this.write(project);
      const projectSummary = this.projectSummaries.find(
        (proj) => proj.id === project.id,
      );
      const projectDetails = this.projectsDetails.find(
        (proj) => proj.id === project.id,
      );

      if (projectSummary) {
        projectSummary.title = project.title;
        projectSummary.number = project.number || null;
        projectSummary.responsible = project.responsible;
        projectSummary.volume = project.volume || 0;
        projectSummary.project_image_url = project.project_image_url;
        projectSummary.is_active = project.is_active;
      }

      if (projectDetails) {
        projectDetails.title = project.title;
        projectDetails.number = project.number || null;
        projectDetails.responsible = project.responsible;
        projectDetails.is_active = project.is_active;
      }

      return persistedProject;
    },

    /**
     * Custom non-working days (WI #5651). Purely presentational: the list only
     * ever feeds the map's shading, so these mutate `project.non_working_days`
     * in place and nothing else in the store reacts to them.
     *
     * There is no fetch action — the list ships inside the project details
     * payload, so it is already loaded by the time the editor opens. The local
     * list is kept sorted by start date to match the order the API returns, so
     * the editor doesn't reshuffle on the next load.
     */
    async createNonWorkingDay(
      projectId: string,
      payload: CreateProjectNonWorkingDayRequest,
    ) {
      const created = await ProjectsService.createProjectNonWorkingDay(
        projectId,
        payload,
      );
      const project = this.project(projectId);
      project.non_working_days = sortNonWorkingDays([
        ...project.non_working_days,
        created,
      ]);

      return created;
    },

    async updateNonWorkingDay(
      projectId: string,
      nonWorkingDayId: string,
      payload: UpdateProjectNonWorkingDayRequest,
    ) {
      const updated = await ProjectsService.updateProjectNonWorkingDay(
        projectId,
        nonWorkingDayId,
        payload,
      );
      const project = this.project(projectId);
      project.non_working_days = sortNonWorkingDays(
        project.non_working_days.map((day) =>
          day.id === nonWorkingDayId ? updated : day,
        ),
      );

      return updated;
    },

    async deleteNonWorkingDay(projectId: string, nonWorkingDayId: string) {
      await ProjectsService.deleteProjectNonWorkingDay(
        projectId,
        nonWorkingDayId,
      );
      const project = this.project(projectId);
      project.non_working_days = project.non_working_days.filter(
        (day) => day.id !== nonWorkingDayId,
      );
    },

    async toggleCalendarSync(projectId: string, enabled: boolean) {
      const body: ProjectCalendarSyncToggleRequest = {
        is_calendar_sync_enabled: enabled,
      };
      const project = await ProjectsService.toggleProjectCalendarSync(
        projectId,
        body,
      );

      return this.write(project);
    },

    async update(projectId: string, body: UpdateProjectRequest) {
      const errors = useProjectRulesStore().validateUpdateProjectRequest(
        projectId,
        body,
      );
      if (errors) {
        throw errors;
      }
      const project = await ProjectsService.updateProject(projectId, body);
      const persistedProject = this.write(project);

      return persistedProject;
    },

    async removeProject(projectId: string) {
      return ProjectsService.removeProject(projectId).then(() => {
        const summaryIdx = this.projectSummaries.findIndex(
          ({ id }) => id === projectId,
        );
        if (summaryIdx > -1) {
          this.projectSummaries.splice(summaryIdx, 1);
        }
        const detailsIndex = this.projectsDetails.findIndex(
          ({ id }) => id === projectId,
        );
        if (detailsIndex > -1) {
          this.projectsDetails.splice(detailsIndex, 1);
        }
      });
    },

    async addDocument(projectId: string, body: CreateDocumentRequest) {
      const document = await ProjectsService.addDocument(projectId, body);
      this.project(projectId).documents.push(document);

      if (body.activity_id) {
        // update the activity documents since the document was created inside an activity;
        // the backend links it without input and as no output
        const activity = useActivityProjectStore().activity(
          projectId,
          body.activity_id,
        );
        activity.documents.push({
          id: document.id,
          input: null,
          output: false,
        });
      }

      return document;
    },

    async updateProjectOwner(projectId: string, newOwnerId: string) {
      const { responsible } = await ProjectsService.updateProjectResponsible(
        projectId,
        { responsible: newOwnerId },
      );
      const project = this.projectDetails(projectId);
      project.responsible = responsible;
    },

    async updateDocument(
      projectId: string,
      documentId: string,
      body: UpdateDocumentRequest,
    ) {
      const updatedDocument = await ProjectsService.updateProjectDocument(
        projectId,
        documentId,
        body,
      );

      const project = this.project(projectId);
      const document = project.documents.find(
        ({ id }) => id === updatedDocument.id,
      );

      if (!document) {
        throw new Error(`No document ${documentId} in project`);
      }
      Object.assign(document, updatedDocument);

      return document;
    },

    async removeDocument(projectId: string, documentId: string) {
      await ProjectsService.removeProjectDocument(projectId, documentId);

      const documents = this.project(projectId).documents;
      const docIndex = documents.findIndex((doc) => doc.id === documentId);

      if (docIndex > -1) {
        documents.splice(docIndex, 1);
      }
    },

    deleteUnreferencedDocuments(projectId: string): Promise<number> {
      return ProjectsService.deleteUnreferencedDocuments(projectId).finally(
        () => this.loadProject(projectId),
      );
    },

    async fetchDocuments(projectId: string) {
      const { documents } = await ProjectsService.getDocuments(projectId);

      const project = this.project(projectId);
      project.documents = documents;

      return documents;
    },

    async addFunction(projectId: string, body: ProjFunctionRequest) {
      const func = await ProjectsService.addFunction(projectId, body);
      this.project(projectId).functions.push(func);
    },

    async mergeFunction(
      projectId: string,
      functionId: string,
      requestBody: MergeFunctionRequest,
    ) {
      return ProjectsService.mergeFunction(
        projectId,
        functionId,
        requestBody,
      ).then((func) => {
        const funcToBeUpdated = this.projectFunction(projectId, functionId);
        Object.assign(funcToBeUpdated, func);
        const project = this.project(projectId);
        const functionIndex = project.functions.findIndex(
          (func) => func.id === requestBody.function_id,
        );
        if (functionIndex > -1) {
          project.functions.splice(functionIndex, 1);
        }
      });
    },

    async updateProjFunction(
      projectId: string,
      functionId: string,
      body: UpdateProjFunctionRequest,
    ) {
      const oldFunction = this.projectFunction(projectId, functionId);
      if (!oldFunction) {
        throw new Error(`No function ${functionId} in project`);
      }
      const oldFunctionEnhanced = Object.assign(
        { user_id: oldFunction.user?.id || null },
        oldFunction,
      );
      const delta = diff(oldFunctionEnhanced, body, true);

      const func = await ProjectsService.updateProjFunction(
        projectId,
        functionId,
        delta,
      );
      this.writeFunc(projectId, func);
    },

    writeFunc(projectId: string, func: ProjFunction) {
      const project = this.project(projectId);
      if (!project) {
        throw new Error('Project is missing in store');
      }
      const persistedFunc = project.functions.find(({ id }) => id === func.id);
      if (!persistedFunc) {
        project.functions.push(func);
      } else {
        Object.assign(persistedFunc, func);
      }

      return persistedFunc;
    },

    async deleteFunction(projectId: string, functionId: string) {
      return await ProjectsService.removeProjectFunction(
        projectId,
        functionId,
      ).then(() => {
        const project = this.project(projectId);
        const functionIndex = project.functions.findIndex(
          (func) => func.id === functionId,
        );
        if (functionIndex > -1) {
          project.functions.splice(functionIndex, 1);
        }
      });
    },

    async loadMembers(projectId: string) {
      const members = await ProjectsService.getMembers(projectId);
      this.$state.members[projectId] = members;

      return this.projectMembers(projectId);
    },

    async addMembers(projectId: string, memberIds: string[]) {
      await ProjectsService.addMembers(projectId, { user_ids: memberIds });
      const userStore = useUserStore();
      const members = memberIds.map((memberId) => userStore.user(memberId));
      this.projectMembers(projectId).push(...members);
    },

    async removeMember(projectId: string, memberId: string) {
      await ProjectsService.removeMember(projectId, memberId);
      const members = this.projectMembers(projectId);
      const memberIndex = members.findIndex((member) => member.id === memberId);
      if (memberIndex) {
        members.splice(memberIndex, 1);
      }

      const editors = this.project(projectId).editor_ids;
      const index = editors.findIndex((id) => id === memberId);
      if (index > -1) {
        editors.splice(index, 1);
      }
    },

    async leaveProject(projectId: string) {
      await ProjectsService.leaveProject(projectId);
    },

    async addOrRemoveEditor(projectId: string, editorId: string) {
      const editors = this.project(projectId).editor_ids;
      await ProjectsService.addRemoveUserToEditor(projectId, editorId);
      const index = editors.findIndex((id) => id === editorId);
      if (index > -1) {
        editors.splice(index, 1);
      } else {
        editors.push(editorId);
      }
    },

    async fetchLinks(projectId: string) {
      const { links } = await ProjectsService.getProjectLinks(projectId);
      const project = this.project(projectId);
      project.links = links;

      return links;
    },

    async createLink(projectId: string, payload: CreateLinkRequest) {
      const errors = useProjectRulesStore().validateAddLinkRequest(
        projectId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const newLink = await ProjectsService.createProjectLink(
        projectId,
        payload,
      );

      if (payload.activity_id) {
        await this.fetchLinks(projectId);
        const activity = useActivityProjectStore().activity(
          projectId,
          payload.activity_id,
        );
        activity.links.push(newLink.id);

        return;
      }

      const project = this.project(projectId);
      project.links.push(newLink);
    },

    async updateLink(
      projectId: string,
      linkId: string,
      payload: UpdateLinkRequest,
    ) {
      const errors = useProjectRulesStore().validateUpdateLinkRequest(
        projectId,
        payload,
        linkId,
      );
      if (errors) {
        throw errors;
      }
      const updatedLink = await ProjectsService.updateProjectLink(
        projectId,
        linkId,
        payload,
      );
      const project = this.project(projectId);
      const oldLink = project.links.find(({ id }) => id === linkId);
      if (oldLink) {
        Object.assign(oldLink, updatedLink);
      } else {
        project.links.push(updatedLink);
      }
    },

    async deleteLink(projectId: string, linkId: string) {
      const errors = useProjectRulesStore().validateRemoveLinkRequest(
        projectId,
        linkId,
      );
      if (errors) {
        throw errors;
      }
      await ProjectsService.deleteProjectLink(projectId, linkId);

      const project = this.project(projectId);
      const removedLinkIndex = project.links.findIndex(
        ({ id }) => id === linkId,
      );

      if (removedLinkIndex > -1) {
        project.links.splice(removedLinkIndex, 1);
      }
    },

    deleteUnreferencedLinks(projectId: string): Promise<number> {
      return ProjectsService.deleteUnreferencedLinks(projectId).finally(() =>
        this.loadProject(projectId),
      );
    },

    async fetchTags(projectId: string) {
      const { tags } = await ProjectsService.getProjectTags(projectId);
      const project = this.project(projectId);
      project.tags = tags.sort((a, b) => a.title.localeCompare(b.title));

      return tags;
    },

    async deleteActivity(projectId: string, activityId: string) {
      await ProjectsService.deleteProjectsActivity(projectId, activityId);
    },

    async removeActivityPredecessor(
      projectId: string,
      activityId: string,
      predecessorId: string,
    ) {
      await ProjectsService.activityRemovePredecessor(
        projectId,
        activityId,
        predecessorId,
      );
    },

    async addAction(
      projectId: string,
      activityId: string,
      payload: ActionRequest,
    ) {
      const action = await ProjectsService.addAction(
        projectId,
        activityId,
        payload,
      );

      this.writeAction(projectId, activityId, action);

      // Returns the created action (not the activity's action list) so callers
      // needing its id can use it — project mail (WI #5643) links it back to the
      // mail it came from. Existing callers ignore the return value.
      return action;
    },

    async addCompletionCriterion(
      projectId: string,
      activityId: string,
      payload: CompletionCriterionRequest,
    ) {
      const criterion = await ProjectsService.addCompletionCriteria(
        projectId,
        activityId,
        payload,
      );

      this.writeCompletionCriterion(projectId, activityId, criterion);

      // Returns the created criterion (not the activity's criteria list) for the
      // same reason as addAction above.
      return criterion;
    },

    async updateCompletionCriterion(
      projectId: string,
      activityId: string,
      completionCriterionId: string,
      payload: CompletionCriterionUpdateRequest,
    ) {
      const criterion = await ProjectsService.activityUpdateCompletionCriterion(
        projectId,
        activityId,
        completionCriterionId,
        payload,
      );

      return this.writeCompletionCriterion(projectId, activityId, criterion);
    },

    async moveCompletionCriterion(
      projectId: string,
      sourceActivityId: string,
      completionCriterionId: string,
      payload: MoveCompletionCriterionRequest,
    ) {
      const criterion = await ProjectsService.moveCompletionCriterion(
        projectId,
        sourceActivityId,
        completionCriterionId,
        payload,
      );
      this.removeTaskLocally(
        projectId,
        sourceActivityId,
        'completion_criteria',
        completionCriterionId,
      );
      // The server's copy, as the move can change its deadline; written to
      // the target's cached details too (WI #5801)
      this.writeCompletionCriterion(
        projectId,
        payload.target_activity_id,
        criterion,
      );
    },

    async moveAction(
      projectId: string,
      sourceActivityId: string,
      actionId: string,
      payload: MoveActionRequest,
    ) {
      const action = await ProjectsService.moveAction(
        projectId,
        sourceActivityId,
        actionId,
        payload,
      );
      this.removeTaskLocally(projectId, sourceActivityId, 'actions', actionId);
      // The server's copy, as the move can change its deadline; written to
      // the target's cached details too (WI #5801)
      this.writeAction(projectId, payload.target_activity_id, action);
    },

    async _updateTaskOrder(
      projectId: string,
      activityId: string,
      order: string[],
      orderType: 'completion_criteria_order' | 'actions_order',
    ) {
      const payload = {
        [orderType]: order,
      };
      const errors = useProjectRulesStore().validateUpdateActivityRequest(
        projectId,
        activityId,
        payload,
      );

      if (errors) {
        throw errors;
      }

      await ProjectsService.updateProjectsActivity(
        projectId,
        activityId,
        payload,
      ).then((activity) => {
        // Cached details keep their own copy of the order (WI #5801);
        // write() updates the summary as well
        const activityProjectStore = useActivityProjectStore();
        if (activityProjectStore.hasActivity(projectId, activityId)) {
          activityProjectStore.write(projectId, activity);
        } else {
          this.writeActivitySummary(projectId, activity);
        }
      });
    },

    async updateCompletionCriteriaOrder(
      projectId: string,
      activityId: string,
      order: string[],
    ) {
      return this._updateTaskOrder(
        projectId,
        activityId,
        order,
        'completion_criteria_order',
      );
    },

    async updateActionsOrder(
      projectId: string,
      activityId: string,
      order: string[],
    ) {
      return this._updateTaskOrder(
        projectId,
        activityId,
        order,
        'actions_order',
      );
    },

    async updateAction(
      projectId: string,
      activityId: string,
      actionId: string,
      payload: ActionUpdateRequest,
    ) {
      const action = await ProjectsService.activityUpdateAction(
        projectId,
        activityId,
        actionId,
        payload,
      );

      return this.writeAction(projectId, activityId, action);
    },

    async removeCompletionCriterion(
      projectId: string,
      activityId: string,
      criterionId: string,
    ) {
      const errors = useProjectRulesStore().validateRemoveCCRequest(
        projectId,
        activityId,
        criterionId,
      );
      if (errors) {
        throw errors;
      }

      await ProjectsService.activityRemoveCompletionCriterion(
        projectId,
        activityId,
        criterionId,
      );
      this.removeTaskLocally(
        projectId,
        activityId,
        'completion_criteria',
        criterionId,
      );
    },

    async removeAction(
      projectId: string,
      activityId: string,
      actionId: string,
    ) {
      const errors = useProjectRulesStore().validateRemoveActionRequest(
        projectId,
        activityId,
        actionId,
      );
      if (errors) {
        throw errors;
      }

      await ProjectsService.activityRemoveAction(
        projectId,
        activityId,
        actionId,
      );
      this.removeTaskLocally(projectId, activityId, 'actions', actionId);
    },

    // MILESTONES
    async updateMilestoneDeadline(
      projectId: string,
      milestoneId: string,
      deadline: string,
    ) {
      await ProjectsService.updateProjectMilestone(projectId, milestoneId, {
        deadline,
      })
        .then((res) => {
          return res;
        })
        .catch((error) => {
          console.log(error);
          alert(error.message);
        });
    },

    // PHASES
    writePhase(projectId: string, phase: Phase, before?: string) {
      const project = this.project(projectId);

      const existing = project.phases.find(({ id }) => id === phase.id);
      if (!existing) {
        const beforeIndex = project.phases.findIndex(({ id }) => id === before);
        if (beforeIndex === -1) {
          project.phases.push(phase);
        } else {
          project.phases.splice(beforeIndex, 0, phase);
        }
      } else {
        Object.assign(existing, phase);
      }

      return existing || phase;
    },

    async updatePhase(
      projectId: string,
      phaseId: string,
      payload: PhaseUpdate,
    ) {
      await ProjectsService.updateProjectPhase(projectId, phaseId, payload)
        .then((phase) => {
          this.writePhase(projectId, phase);

          return phase;
        })
        .catch((error) => {
          console.log(error);
          alert(error.message);
        });
    },

    async createPhase(
      projectId: string,
      payload: PhaseCreate,
      beforeId?: string,
    ) {
      await ProjectsService.addProjectPhase(projectId, payload, beforeId);
    },

    // WORKSTREAMS
    writeWorkstream(
      projectId: string,
      workstream: Workstream,
      before?: string,
    ) {
      const project = this.project(projectId);
      const existing = project.workstreams.find(
        ({ id }) => id === workstream.id,
      );
      if (!existing) {
        const beforeIndex = project.workstreams.findIndex(
          ({ id }) => id === before,
        );
        if (beforeIndex === -1) {
          project.workstreams.push(workstream);
        } else {
          project.workstreams.splice(beforeIndex, 0, workstream);
        }
      } else {
        Object.assign(existing, workstream);
      }

      return existing || workstream;
    },

    async addWorkstream(
      projectId: string,
      payload: WorkstreamCreate,
      before?: string,
    ) {
      const errors = useProjectRulesStore().validateAddWorkstreamRequest(
        projectId,
        payload,
        before,
      );
      if (errors) {
        throw errors;
      }
      const workstream = await ProjectsService.addProjectWorkstream(
        projectId,
        payload,
        before,
      );

      return this.writeWorkstream(projectId, workstream, before);
    },

    async addWorkstreamFromTemplate(
      projectId: string,
      payload: AddWorkstreamFromTemplateRequest,
    ) {
      await ProjectsService.addWorkstreamFromTemplate(projectId, payload);

      // Reload the project to get the updated workstreams
      await this.loadProject(projectId);
    },

    async updateWorkstream(
      projectId: string,
      workstreamId: string,
      payload: Partial<Workstream>,
    ) {
      const errors = useProjectRulesStore().validateUpdateWorkstreamRequest(
        projectId,
        payload,
        workstreamId,
      );
      if (errors) {
        throw errors;
      }
      const workstream = await ProjectsService.updateProjectWorkstream(
        projectId,
        workstreamId,
        payload,
      );

      return this.writeWorkstream(projectId, workstream);
    },

    async deleteWorkstream(projectId: string, workstreamId: string) {
      const errors = useProjectRulesStore().validateRemoveWorkstreamRequest(
        projectId,
        workstreamId,
      );
      if (errors) {
        throw errors;
      }
      await ProjectsService.removeProjectWorkstream(projectId, workstreamId);
      const project = this.project(projectId);
      const index = project.workstreams.findIndex(
        ({ id }) => id === workstreamId,
      );
      project.workstreams.splice(index, 1);
    },

    async applySpaceTool(
      projectId: string,
      payload: ProjectShiftElementsRequest,
    ) {
      await ProjectsService.shiftProjectElements(projectId, payload);
      await this.loadProject(projectId);
    },

    async removeMirroredActivity(
      projectId: string,
      mirroredActivityId: string,
    ) {
      await ProjectsService.removeMirroredActivity(
        projectId,
        mirroredActivityId,
      );
    },

    async updateMirroredActivity(
      projectId: string,
      mirroredActivityId: string,
      payload: UpdateMirrorActivityRequest,
    ) {
      await ProjectsService.updateMirroredActivity(
        projectId,
        mirroredActivityId,
        payload,
      );
      await this.writeMirroredActivitySummary(
        projectId,
        mirroredActivityId,
        payload.target_workstream_id,
      );
    },

    async addMirroredActivityRelation(
      projectId: string,
      mirroredActivityId: string,
      payload: MirrorActivityRelationRequest,
    ) {
      await ProjectsService.mirrorActivityRelation(
        projectId,
        mirroredActivityId,
        payload,
      );
    },

    async removeMirroredActivityRelation(
      projectId: string,
      mirroredActivityId: string,
      payload: RemoveMirrorActivityRelationRequest,
    ) {
      await ProjectsService.removeMirroredActivityRelation(
        projectId,
        mirroredActivityId,
        payload,
      );
    },

    async removeMirroredMilestone(
      projectId: string,
      mirroredMilestoneId: string,
    ) {
      await ProjectsService.removeMirroredMilestone(
        projectId,
        mirroredMilestoneId,
      );
    },

    mirrorActivitiesIntoProject(
      projectId: string,
      payload: {
        add_mirrors: MirrorActivityRequest[];
        remove_mirrors: string[];
      },
    ) {
      return ProjectsService.mirrorActivitiesIntoProject(projectId, payload);
    },

    mirrorMilestonesIntoProject(
      projectId: string,
      payload: {
        add_mirrors: MirrorMilestoneRequest[];
        remove_mirrors: string[];
      },
    ) {
      return ProjectsService.mirrorMilestonesIntoProject(projectId, payload);
    },

    async toggleFavorite(projectId: string) {
      const profileStore = useProfileStore();
      if (this.favoriteProjectIds.includes(projectId)) {
        this.favoriteProjectIds = this.favoriteProjectIds.filter(
          (id) => id !== projectId,
        );
        this.lastToggledProjectId = '';
      } else {
        this.favoriteProjectIds.push(projectId);
        this.lastToggledProjectId = projectId;
      }

      profileStore.settings.project_favorites = [...this.favoriteProjectIds];

      await profileStore.saveUserSettings(toRaw(profileStore.settings));
      this.favoriteProjectIds = [...profileStore.settings.project_favorites];
    },

    async performSemanticSearch(projectId: string, query: string) {
      return ProjectsService.searchProject(projectId, query);
    },

    loadProjectImage(projectId: string, noCache = false): void {
      if (!noCache && projectId in this.imageUrls) {
        return;
      }

      enqueueProjectImageFetch(async () => {
        try {
          const oldUrl = this.imageUrls[projectId];
          if (oldUrl) {
            URL.revokeObjectURL(oldUrl);
          }
          this.imageUrls[projectId] = await fetchProjectImageBlobUrl(
            projectId,
            noCache,
          );
        } catch {
          this.imageUrls[projectId] = null;
        }
      });
    },
  },
});

function mapCCOrActionDetailsToCCOrAction(
  criterion: CompletionCriterionDetails | ActionDetails,
): CompletionCriterion | Action {
  return {
    id: criterion.id,
    title: criterion.title,
    description: criterion.description,
    initially_planned: criterion.initially_planned,
    current_deadline: criterion.current_deadline,
    manual_due_date: criterion.manual_due_date,
    state: criterion.state,
    status: criterion.status,
    responsible: criterion.responsible,
    completion_user: criterion.completion_user,
    comments: criterion.comments,
    meeting_references: criterion.meeting_references,
  };
}
